import type { Role } from '@/types/domain';

// RBAC capability matrix (Constitución: Menor privilegio).
// This mirrors — but does NOT replace — Postgres Row Level Security.
// RLS is the source of truth; this gates UI affordances for a clean UX.

export type Capability =
  | 'clients.read'
  | 'clients.write'
  | 'documents.read'
  | 'documents.write'
  | 'documents.download'
  | 'tax_returns.read'
  | 'tax_returns.write'
  | 'portal.manage'
  | 'notarization.request'
  | 'notarization.sign'
  | 'payments.read'
  | 'payments.manage'
  | 'subscriptions.manage'
  | 'ai.use'
  | 'settings.manage'
  | 'security.manage'
  | 'audit.read'
  | 'reports.read'
  | 'sandbox.use';

const ALL: Capability[] = [
  'clients.read', 'clients.write', 'documents.read', 'documents.write',
  'documents.download', 'tax_returns.read', 'tax_returns.write', 'portal.manage',
  'notarization.request', 'notarization.sign', 'payments.read', 'payments.manage',
  'subscriptions.manage', 'ai.use', 'settings.manage', 'security.manage',
  'audit.read', 'reports.read', 'sandbox.use',
];

export const ROLE_CAPABILITIES: Record<Role, Capability[]> = {
  super_admin: ALL,
  admin: ALL.filter((c) => c !== 'security.manage' || true), // admin: full ops, security shared
  tax_preparer: [
    'clients.read', 'clients.write', 'documents.read', 'documents.write',
    'documents.download', 'tax_returns.read', 'tax_returns.write', 'portal.manage',
    'ai.use', 'reports.read', 'sandbox.use',
  ],
  notary: [
    'documents.read', 'notarization.sign', 'payments.read', 'ai.use', 'sandbox.use',
  ],
  client: [
    'documents.read', 'documents.write', 'tax_returns.read',
    'notarization.request', 'ai.use',
  ],
  support: [
    'clients.read', 'documents.read', 'tax_returns.read', 'ai.use', 'reports.read',
  ],
  auditor: [
    'clients.read', 'documents.read', 'tax_returns.read', 'payments.read',
    'audit.read', 'reports.read',
  ],
};

export function can(role: Role | undefined, capability: Capability): boolean {
  if (!role) return false;
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false;
}
